import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SeoMetaOptions {
  pageType: string;
  entityId?: string;
  variables?: Record<string, string>;
  canonicalPath?: string;
}

function replaceVariables(template: string, variables: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] ?? "");
}

export function useSeoMeta({ pageType, entityId, variables = {}, canonicalPath }: SeoMetaOptions) {
  const { data: globalSettings } = useQuery({
    queryKey: ["seo-global"],
    queryFn: async () => {
      const { data } = await supabase
        .from("seo_global_settings")
        .select("*")
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: template } = useQuery({
    queryKey: ["seo-template", pageType],
    queryFn: async () => {
      const { data } = await supabase
        .from("seo_templates")
        .select("*")
        .eq("page_type", pageType)
        .eq("is_active", true)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: override } = useQuery({
    queryKey: ["seo-override", pageType, entityId],
    queryFn: async () => {
      let query = supabase
        .from("seo_overrides")
        .select("*")
        .eq("page_type", pageType);
      if (entityId) {
        query = query.eq("entity_id", entityId);
      } else {
        query = query.is("entity_id", null);
      }
      const { data } = await query.maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 30,
    enabled: !!pageType,
  });

  useEffect(() => {
    if (!globalSettings) return;

    // Resolution: Override > Template > Global
    const title = override?.custom_title
      ? override.custom_title
      : template?.title_template
        ? replaceVariables(template.title_template, variables) + " " + (globalSettings.title_suffix ?? "")
        : globalSettings.site_name;

    const description = override?.custom_meta_description
      ?? (template?.meta_description_template
        ? replaceVariables(template.meta_description_template, variables)
        : globalSettings.default_meta_description);

    const robots = override?.custom_robots_directive
      ?? template?.robots_directive
      ?? globalSettings.default_robots;

    document.title = title.trim();

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) setMeta("description", description);
    if (robots) setMeta("robots", robots);

    // Canonical link
    const canonicalUrl = canonicalPath
      ? `${window.location.origin}${canonicalPath}`
      : override?.custom_canonical
        ?? (template?.canonical_template
          ? `${window.location.origin}${replaceVariables(template.canonical_template, variables)}`
          : undefined);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
    }

    return () => {
      document.title = globalSettings.site_name ?? "Bandflow";
      // Clean up canonical
      const link = document.querySelector('link[rel="canonical"]');
      if (link) link.remove();
    };
  }, [globalSettings, template, override, variables, canonicalPath]);
}
